import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  Type,
} from '@angular/core';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import {
  FieldTypeConfig,
  FormlyFieldConfig,
  ɵobserve as observe,
} from '@ngx-formly/core';
import {
  FormlyFieldSelectProps,
  FormlySelectOption,
} from '@ngx-formly/core/select';
import { FieldType } from '@ngx-formly/material';
import { FormlyFieldProps } from '@ngx-formly/material/form-field';
import {
  combineLatest,
  map,
  Observable,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  withLatestFrom,
} from 'rxjs';
import {
  SelectOption,
  SelectOptionsPipe,
} from '../pipes/select-options/select-options.pipe';
import { some } from 'lodash';

interface AutocompleteProps extends FormlyFieldProps, FormlyFieldSelectProps {
  multiple?: boolean;
  selectAllOption?: string;
  disableOptionCentering?: boolean;
  typeaheadDebounceInterval?: number;
  autocomplete?: string;
  compareWith?: (o1: unknown, o2: unknown) => boolean;
  forceOptionsValue?: boolean;
}

export interface AutocompleteFieldConfig
  extends FormlyFieldConfig<AutocompleteProps> {
  type: 'autocomplete' | Type<AutocompleteTypeComponent>;
}

@Component({
    selector: 'lockton-apps-autocomplete-type',
    templateUrl: './autocomplete-type.component.html',
    styleUrls: ['./autocomplete-type.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AutocompleteTypeComponent
  extends FieldType<FieldTypeConfig<AutocompleteProps>>
  implements OnInit, OnDestroy
{
  _formlySelectOptions$!: Observable<FormlySelectOption[]>;

  optionsChanged$ = new Subject<void>();

  displayWith$!: Observable<(value: any) => string>;

  autocompleteOpen$ = new Subject<boolean>();

  inputFocus$ = new Subject<boolean>();

  destroyed$ = new Subject<void>();

  get formlySelectOptions$(): Observable<FormlySelectOption[]> {
    return this.optionsChanged$.asObservable().pipe(
      startWith(null),
      switchMap(() => this._formlySelectOptions$)
    );
  }

  constructor(private selectOptionsPipe: SelectOptionsPipe) {
    super();
  }

  ngOnInit(): void {
    observe(this.field, ['props', 'options'], () => {
      this._formlySelectOptions$ = this.selectOptionsPipe.transform(
        this.props.options,
        this.field
      );
      this.optionsChanged$.next();
    });

    this.displayWith$ = this.formlySelectOptions$.pipe(
      map((options) => {
        const flattened = this._flattenOptions(options);
        return this._displayWithFn(flattened);
      })
    );

    if (this.props.forceOptionsValue) {
      this.applyForceOptionsValue();
    }
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.destroyed$.next();
    this.inputFocus$.complete();
    this.destroyed$.complete();
    this.autocompleteOpen$.complete();
  }

  onOptionSelected(e: MatAutocompleteSelectedEvent): void {
    this.formControl.setValue(e.option.value);
  }

  private compareWith(o1: unknown, o2: unknown): boolean {
    return this.props.compareWith ? this.props.compareWith(o1, o2) : o1 === o2;
  }

  private _displayWithFn(
    options: (FormlySelectOption | SelectOption)[]
  ): (value: any) => string {
    return (value: any) => {
      const matched = options.find((option) => {
        return (
          this.compareWith(option.value, value) ||
          (option as SelectOption).description === value
        );
      });
      return matched ? matched.label : '';
    };
  }

  private _flattenOptions(options: FormlySelectOption[]) {
    return options.reduce((flattended, option) => {
      const groupedOptions = option.group !== undefined ? option.group : [];

      return [...flattended, option, ...groupedOptions];
    }, [] as FormlySelectOption[]);
  }

  /** Forces value to be within options list otherwise clears on blur */
  private applyForceOptionsValue() {
    combineLatest([this.autocompleteOpen$.asObservable(), this.inputFocus$])
      .pipe(
        withLatestFrom(this.formlySelectOptions$),
        takeUntil(this.destroyed$)
      )
      .subscribe(([[isOpen, isFocused], options]) => {
        const valueSelected = some(
          options,
          (option) => option.value === this.formControl.value
        );

        if (!isOpen && !isFocused && !valueSelected) {
          this.formControl.reset();
        }
      });
  }
}
